import { useEffect, useState } from "react";
import { Loader2, CalendarCheck, CalendarDays, GanttChartSquare, List } from "lucide-react";
import { MonthCalendar } from "@/components/bookings/MonthCalendar";
import { WeekTimeline } from "@/components/bookings/WeekTimeline";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const statusColor = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-700",
};

export default function BookingsPage() {
  const [view, setView] = useState("month");
  const views = [{ id: "month", label: "Calendar", icon: CalendarDays }, { id: "week", label: "Timeline", icon: GanttChartSquare }, { id: "list", label: "Requests", icon: List }];
  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl text-foreground mb-1">Bookings</h1>
          <p className="text-muted-foreground text-sm">Direct bookings, channel reservations, tables and check-ins in one place.</p>
        </div>
        <div className="inline-flex bg-secondary rounded-full p-1">
          {views.map((v) => (
            <button key={v.id} onClick={() => setView(v.id)} data-testid={`bookings-view-${v.id}`} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm transition-colors ${view === v.id ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <v.icon className="w-3.5 h-3.5" />{v.label}
            </button>
          ))}
        </div>
      </div>
      {view === "month" && <MonthCalendar />}
      {view === "week" && <WeekTimeline />}
      {view === "list" && <RequestsList />}
    </div>
  );
}

function RequestsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/bookings").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.put(`/bookings/${id}`, { status });
    toast.success(`Marked ${status}`);
    load();
  };

  const filtered = filter === "all" ? items : items.filter((b) => b.status === filter);

  return (
    <div className="space-y-6" data-testid="bookings-requests">
      <div className="flex items-center justify-end flex-wrap gap-4">
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setFilter("all")} data-testid="filter-all" className={`px-3 py-1.5 rounded-md text-sm ${filter === "all" ? "bg-primary text-primary-foreground" : "border border-border"}`}>All</button>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`} className={`px-3 py-1.5 rounded-md text-sm capitalize ${filter === s ? "bg-primary text-primary-foreground" : "border border-border"}`}>{s}</button>
          ))}
        </div>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-accent" /> : filtered.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-md">
          <CalendarCheck className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm">No bookings in this view.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-md divide-y divide-border">
          {filtered.map((b) => (
            <div key={b.id} className="p-4 flex items-center justify-between flex-wrap gap-4" data-testid={`booking-${b.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-foreground font-medium">{b.service_name}</p>
                  <Badge className={`${statusColor[b.status]} border-0 capitalize`}>{b.status}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {b.guest_name} · Room {b.room || "—"}{b.contact ? ` · ${b.contact}` : ""}{b.date ? ` · ${b.date}` : ""}
                </p>
                {b.notes && <p className="text-sm text-muted-foreground mt-1 italic">“{b.notes}”</p>}
              </div>
              <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                <SelectTrigger className="w-40" data-testid={`booking-status-${b.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
